import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { UserProfile } from "@/components/user/UserProfile";
import { EditUserForm } from "@/components/user/EditUserForm";
import { ChangeDepartment } from "@/components/user/ChangeDepartment";
import { ChangeStatus } from "@/components/user/ChangeStatus";
import { AdminPasswordResetDialog } from "@/components/user/AdminPasswordResetDialog";
import { SystemUser } from "../hooks/useUserManagement";

interface UserDialogsProps {
  isProfileDialogOpen: boolean;
  isEditDialogOpen: boolean;
  isChangeDeptDialogOpen: boolean;
  isChangeStatusDialogOpen: boolean;
  isPasswordResetDialogOpen: boolean;
  selectedUser: SystemUser | null;
  handleProfileDialogClose: (open: boolean) => void;
  handleEditDialogClose: (open: boolean) => void;
  handleChangeDeptDialogClose: (open: boolean) => void;
  handleChangeStatusDialogClose: (open: boolean) => void;
  handlePasswordResetDialogClose: (open: boolean) => void;
  saveEditedUser: (updatedUser: SystemUser) => void;
  saveDepartmentChange: (userId: string, newDepartment: string, reason?: string) => void;
  saveStatusChange: (userId: string, newStatus: string, reason?: string) => void;
  onResetPasswordInternal: (userId: string, newPassword: string) => Promise<void>;
}

export function UserDialogs({
  isProfileDialogOpen,
  isEditDialogOpen,
  isChangeDeptDialogOpen,
  isChangeStatusDialogOpen,
  isPasswordResetDialogOpen,
  selectedUser,
  handleProfileDialogClose,
  handleEditDialogClose,
  handleChangeDeptDialogClose,
  handleChangeStatusDialogClose,
  handlePasswordResetDialogClose,
  saveEditedUser,
  saveDepartmentChange,
  saveStatusChange,
  onResetPasswordInternal
}: UserDialogsProps) {
  
  return (
    <>
      {/* User Profile Dialog */}
      <Dialog open={isProfileDialogOpen} onOpenChange={handleProfileDialogClose}>
        <DialogContent className="w-full max-w-lg mx-auto">
          <DialogHeader>
            <DialogTitle>User Profile</DialogTitle>
            <DialogDescription className="sr-only">View user details and information</DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <UserProfile 
              user={selectedUser}
              onClose={() => handleProfileDialogClose(false)} 
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={handleEditDialogClose}>
        <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription className="sr-only">Edit user information</DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <EditUserForm 
              user={selectedUser}
              onSave={saveEditedUser}
              onCancel={() => handleEditDialogClose(false)}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Change Department Dialog */}
      <Dialog open={isChangeDeptDialogOpen} onOpenChange={handleChangeDeptDialogClose}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Change Department</DialogTitle>
            <DialogDescription className="sr-only">Change user's department</DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <ChangeDepartment 
              user={selectedUser}
              onSave={saveDepartmentChange}
              onCancel={() => handleChangeDeptDialogClose(false)}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Change Status Dialog */}
      <Dialog open={isChangeStatusDialogOpen} onOpenChange={handleChangeStatusDialogClose}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Change Status</DialogTitle>
            <DialogDescription className="sr-only">Change user's status</DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <ChangeStatus 
              user={selectedUser}
              onSave={saveStatusChange}
              onCancel={() => handleChangeStatusDialogClose(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      {selectedUser && (
        <AdminPasswordResetDialog
          open={isPasswordResetDialogOpen}
          onOpenChange={handlePasswordResetDialogClose}
          userId={selectedUser.id}
          userName={selectedUser.name}
          onResetPassword={onResetPasswordInternal}
        />
      )}
    </>
  );
} 